import { useMemo } from "react";

import { useQueries } from "@tanstack/react-query";

import {
  VirtualAccount,
  keyMaker as virtualAccountKeyMaker,
} from "@fartherfinance/frontend/api/Accounts/hooks/useVirtualAccount";
import {
  HookResult,
  VirtualAccountId,
} from "@fartherfinance/frontend/api/Types";

function isVirtualAccount(account: any): account is VirtualAccount {
  return account !== undefined && "virtualAccountId" in account;
}

type AllVirtualAccountsData = HookResult<VirtualAccount[]>;

function useGetAllVirtualAccounts(
  virtualAccountIds: VirtualAccountId[]
): AllVirtualAccountsData {
  const virtualAccountsQueryKeys = useMemo(
    () => virtualAccountIds.map((tpi) => virtualAccountKeyMaker(tpi)),
    [virtualAccountIds]
  );

  const userQueries = useQueries<VirtualAccount[]>({
    queries: virtualAccountsQueryKeys.map((queryKey) => {
      return {
        queryKey: queryKey,
      };
    }),
  });

  const virtualAccountsFetching = useMemo(
    () => userQueries.some((query) => query.isFetched === false),
    [userQueries]
  );

  const accountErrors = useMemo(
    () => userQueries.some((query) => query.isError === true),
    [userQueries]
  );

  return useMemo(() => {
    if (virtualAccountsFetching) {
      return {
        isLoading: true,
        data: undefined,
        hasError: false,
      };
    }

    if (accountErrors) {
      return {
        isLoading: false,
        data: undefined,
        hasError: true,
        error: new Error("Error fetching virtual accounts"),
      };
    }

    const allVirtualAccounts = userQueries
      .map((query) => query.data)
      .filter(isVirtualAccount);

    return {
      isLoading: false,
      data: allVirtualAccounts,
      hasError: false,
    };
  }, [virtualAccountsFetching, accountErrors, userQueries]);
}

export default useGetAllVirtualAccounts;
